// Pure scoring functions — no React, importable anywhere.

// mode: 'combined' | 'chat' | 'protege' | 'workflows' | 'live'
function _msgs(row, mode = 'combined') {
  if (mode === 'chat')      return row.chat_messages    ?? 0;
  if (mode === 'protege')   return row.protege_messages ?? 0;
  if (mode === 'workflows') return (row.preset_workflows ?? 0) + (row.custom_workflows ?? 0);
  if (mode === 'live')      return row.live_events       ?? 0;
  // combined: all 4 feature types
  return (row.chat_messages ?? 0) + (row.protege_messages ?? 0) +
         (row.live_events   ?? 0) + (row.preset_workflows ?? 0) + (row.custom_workflows ?? 0);
}

export function last6Months() {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

/**
 * Compute engagement score (0–100) for a single user.
 * Returns { score, tier, breakdown }
 */
export function computeUserScore(userMonthly, uid, mode = 'combined') {
  const now         = new Date();
  const dayOfMonth  = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const grossup     = dayOfMonth >= 7 ? daysInMonth / dayOfMonth : 1;
  const months      = last6Months();
  const currentM    = months[months.length - 1];

  const mdata = {};
  for (const r of userMonthly) {
    if (r.user_id !== uid) continue;
    const raw = _msgs(r, mode);
    const est = r.year_month === currentM && dayOfMonth >= 7 ? Math.round(raw * grossup) : raw;
    mdata[r.year_month] = est;
  }

  // Recency (30 pts)
  let lastActiveIdx = -1;
  for (let i = months.length - 1; i >= 0; i--) {
    if ((mdata[months[i]] || 0) > 0) { lastActiveIdx = i; break; }
  }
  const monthsAgo = lastActiveIdx === -1 ? 99 : (months.length - 1 - lastActiveIdx);
  let recencyPts, recencyLabel;
  if      (monthsAgo === 0) { recencyPts = 30; recencyLabel = 'Active this month'; }
  else if (monthsAgo === 1) { recencyPts = 20; recencyLabel = 'Active last month'; }
  else if (monthsAgo === 2) { recencyPts = 10; recencyLabel = 'Active 2mo ago'; }
  else if (monthsAgo <= 4)  { recencyPts = 3;  recencyLabel = `Active ${monthsAgo}mo ago`; }
  else                       { recencyPts = 0;  recencyLabel = 'No recent activity'; }

  // Volume (25 pts) — most recent active month
  const recentMsgs = lastActiveIdx >= 0 ? (mdata[months[lastActiveIdx]] || 0) : 0;
  let volumePts;
  if      (recentMsgs >= 50) volumePts = 25;
  else if (recentMsgs >= 20) volumePts = 20;
  else if (recentMsgs >= 10) volumePts = 14;
  else if (recentMsgs >= 5)  volumePts = 8;
  else if (recentMsgs >= 1)  volumePts = 4;
  else                        volumePts = 0;

  // Trend (25 pts) — recent-2mo avg vs prior-2mo avg over the last 4 complete
  // months (current partial month excluded), not just a single-month-over-
  // single-month comparison. A 2-month bounce after a longer decline still
  // nets out negative here instead of registering as a spike.
  //
  // Below TREND_MIN_BASELINE the prior-window average is too small for a %
  // change to mean anything — e.g. 2 -> 5 messages is a real 150% "MoM" by
  // the raw formula but is noise, not a re-engagement signal. Below that
  // floor we fall back to a small flat-direction bump instead of the full
  // percentage-derived score.
  const TREND_WINDOW_MONTHS = 4;
  const TREND_MIN_BASELINE = 5;
  const histMonths = [];
  for (let i = 2; i <= TREND_WINDOW_MONTHS + 1; i++) {
    histMonths.push(mdata[months[months.length - i]] || 0);
  }
  const half = Math.floor(histMonths.length / 2);
  const recentAvg = histMonths.slice(0, half).reduce((a, b) => a + b, 0) / half;
  const earlierAvg = histMonths.slice(half).reduce((a, b) => a + b, 0) / half;

  let trendPts, trendLabel;
  if (earlierAvg === 0 && recentAvg === 0) {
    trendPts = 0; trendLabel = 'No prior data';
  } else if (earlierAvg === 0) {
    trendPts = 15; trendLabel = 'New user';
  } else if (earlierAvg < TREND_MIN_BASELINE) {
    trendPts = recentAvg > earlierAvg ? 10 : recentAvg === earlierAvg ? 8 : 5;
    trendLabel = 'Low volume — trend unreliable';
  } else {
    const chg = (recentAvg - earlierAvg) / earlierAvg;
    if      (chg >  0.1)  { trendPts = 25; trendLabel = `+${Math.round(chg * 100)}% (${TREND_WINDOW_MONTHS}mo)`; }
    else if (chg >= -0.1) { trendPts = 18; trendLabel = 'Flat'; }
    else if (chg >= -0.5) { trendPts = 10; trendLabel = `${Math.round(chg * 100)}% (${TREND_WINDOW_MONTHS}mo)`; }
    else                  { trendPts = 3;  trendLabel = `${Math.round(chg * 100)}% (${TREND_WINDOW_MONTHS}mo)`; }
  }

  // Consistency (20 pts) — normalized to months user has been in system (fixes new-user penalty)
  const activeCount    = months.filter((m) => (mdata[m] || 0) > 0).length;
  const firstActiveIdx = months.findIndex((m) => (mdata[m] || 0) > 0);
  const monthsInSystem = firstActiveIdx === -1 ? 6 : months.length - firstActiveIdx;
  const consistencyPts = Math.round((activeCount / Math.max(1, monthsInSystem)) * 20);

  const score = recencyPts + volumePts + trendPts + consistencyPts;
  const tier  = score >= 70 ? 'healthy' : score >= 40 ? 'watch' : score >= 15 ? 'at-risk' : 'churned';

  return {
    score,
    tier,
    breakdown: { recencyPts, volumePts, trendPts, consistencyPts, recencyLabel, trendLabel, recentMsgs, activeCount, monthsInSystem },
  };
}

/**
 * Company-level health score — 3 components:
 *   Adoption  (40pts): % of known users active this month
 *   Depth     (40pts): champion's engagement score / 100
 *   Momentum  (20pts): active-user count trend vs last month
 *
 * Returns { score, tier, adoptionPct, adoptionPts, depthPts, momentumPts,
 *           activeThisMonth, totalUsers, championUid, userCount }
 */
export function computeCompanyScore(userMonthly, domain, mode = 'combined') {
  const now      = new Date();
  const currentM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const prevD    = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevM    = `${prevD.getFullYear()}-${String(prevD.getMonth() + 1).padStart(2, '0')}`;

  const uids = [...new Set(userMonthly.filter((r) => r.domain === domain).map((r) => r.user_id))];
  if (!uids.length) return null;

  // Adoption
  const activeThisMonth = uids.filter((uid) =>
    userMonthly.some((r) => r.user_id === uid && r.year_month === currentM && _msgs(r, mode) > 0)
  ).length;
  const activeLastMonth = uids.filter((uid) =>
    userMonthly.some((r) => r.user_id === uid && r.year_month === prevM && _msgs(r, mode) > 0)
  ).length;
  const adoptionPct = activeThisMonth / uids.length;
  const adoptionPts = Math.round(adoptionPct * 40);

  // Depth — champion score
  const scores     = uids.map((uid) => computeUserScore(userMonthly, uid, mode));
  let champIdx     = 0;
  scores.forEach((s, i) => { if (s.score > scores[champIdx].score) champIdx = i; });
  const maxScore   = scores[champIdx].score;
  const depthPts   = Math.round((maxScore / 100) * 40);
  const championUid = uids[champIdx];

  // Momentum — active-user count trend
  let momentumPts;
  if (activeLastMonth === 0 && activeThisMonth > 0) { momentumPts = 20; }
  else if (activeLastMonth === 0)                   { momentumPts = 10; }
  else {
    const chg = (activeThisMonth - activeLastMonth) / activeLastMonth;
    if      (chg >  0.1)  momentumPts = 20;
    else if (chg >= -0.1) momentumPts = 15;
    else if (chg >= -0.5) momentumPts = 7;
    else                  momentumPts = 2;
  }

  const score = adoptionPts + depthPts + momentumPts;
  const tier  = score >= 70 ? 'healthy' : score >= 40 ? 'watch' : score >= 15 ? 'at-risk' : 'churned';
  const worst = scores.reduce((a, b) => a.score < b.score ? a : b);

  return {
    score, tier, worstTier: worst.tier, userCount: uids.length,
    adoptionPct: Math.round(adoptionPct * 100), adoptionPts, depthPts, momentumPts,
    activeThisMonth, activeLastMonth, totalUsers: uids.length, championUid,
  };
}

/**
 * Compute urgency from a trial end_date string.
 * Returns { label, color, bg, daysLeft } or null if no date.
 */
export function computeUrgency(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (isNaN(end)) return null;
  const daysLeft = Math.round((end.getTime() - Date.now()) / 86_400_000);
  if (daysLeft < 0)  return { label: 'Expired',        color: '#6b7280', bg: '#f3f4f6', daysLeft };
  if (daysLeft <= 14) return { label: `${daysLeft}d left`, color: '#dc2626', bg: '#fee2e2', daysLeft };
  if (daysLeft <= 45) return { label: `${daysLeft}d left`, color: '#d97706', bg: '#fef3c7', daysLeft };
  return               { label: `${daysLeft}d left`, color: '#16a34a', bg: '#dcfce7', daysLeft };
}

/**
 * Build a Map of email (lowercase) → { score, tier, breakdown, lastActiveMonth,
 * monthlyValues, months, currentMonthMsgs, avgMsgsPerMonth }.
 */
export function buildScoresMap(userMonthly, mode = 'combined') {
  const now         = new Date();
  const dayOfMonth  = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const grossup     = dayOfMonth >= 7 ? daysInMonth / dayOfMonth : 1;
  const months      = last6Months();
  const currentM    = months[months.length - 1];

  const uids = [...new Set(userMonthly.map((r) => r.user_id))];
  const map  = {};

  for (const uid of uids) {
    const scoreResult = computeUserScore(userMonthly, uid, mode);

    // Build monthly values array aligned to last6Months()
    const mdata = {};
    for (const r of userMonthly) {
      if (r.user_id !== uid) continue;
      const raw = _msgs(r, mode);
      mdata[r.year_month] = r.year_month === currentM && dayOfMonth >= 7 ? Math.round(raw * grossup) : raw;
    }
    const monthlyValues = months.map((m) => mdata[m] || 0);

    // Last active month label
    let lastActiveMonth = null;
    for (let i = monthlyValues.length - 1; i >= 0; i--) {
      if (monthlyValues[i] > 0) { lastActiveMonth = months[i]; break; }
    }

    const currentMonthMsgs = monthlyValues[monthlyValues.length - 1];
    const activeMsgs       = monthlyValues.filter((v) => v > 0);
    const avgMsgsPerMonth  = activeMsgs.length
      ? Math.round(activeMsgs.reduce((a, b) => a + b, 0) / activeMsgs.length)
      : 0;

    map[uid.toLowerCase()] = {
      ...scoreResult,
      lastActiveMonth,
      monthlyValues,
      months,
      currentMonthMsgs,
      avgMsgsPerMonth,
    };
  }

  return map;
}

/**
 * Global Amplitude stats — median & avg messages this month across all external users.
 * Used for "vs all users" comparison in contact detail.
 */
export function computeAmplitudeGlobalStats(userMonthly, mode = 'combined') {
  const now        = new Date();
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const grossup    = dayOfMonth >= 7 ? daysInMonth / dayOfMonth : 1;
  const currentM   = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const byUid = {};
  for (const r of userMonthly) {
    if (!r.user_id || r.year_month !== currentM) continue;
    const raw = _msgs(r, mode);
    byUid[r.user_id] = dayOfMonth >= 7 ? Math.round(raw * grossup) : raw;
  }

  const values = Object.values(byUid).filter((v) => v > 0).sort((a, b) => a - b);
  if (!values.length) return { median: 0, avg: 0, activeUsers: 0 };

  const median = values[Math.floor(values.length / 2)];
  const avg    = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
  return { median, avg, activeUsers: values.length };
}

export const USAGE_MODES = [
  { value: 'combined',  label: 'All activity' },
  { value: 'chat',      label: 'Chat' },
  { value: 'protege',   label: 'Protege' },
  { value: 'workflows', label: 'Workflows' },
  { value: 'live',      label: 'Live events' },
];

export const SCORE_TIER_COLOR = { healthy: '#16a34a', watch: '#d97706', 'at-risk': '#ea580c', churned: '#dc2626' };
export const SCORE_TIER_BG    = { healthy: '#dcfce7', watch: '#fef3c7', 'at-risk': '#ffedd5', churned: '#fee2e2' };
export const SCORE_TIER_LABEL = { healthy: 'Healthy', watch: 'Watch',   'at-risk': 'At risk',  churned: 'Churned' };
